import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { LoadingOverlay, RECEIVABLES_API, VISIT_DAYS } from "./common";

/**
 * Which weekday each beat is worked (legacy day_assignment.aspx). "Assigned beats" shows every beat that already has a day,
 * with the selected day's beats ticked — ticking moves a beat to this day, unticking frees it. "Unassigned beats" lists beats
 * with no day so they can be added to the selected day.
 */
const DayAssignment = () => {
  const [rows, setRows] = useState([]);
  const [day, setDay] = useState("MON");
  const [mode, setMode] = useState("assigned");
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.get(`${RECEIVABLES_API}/beat-day-assignments`);
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message || "Failed to load beats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setChecked(mode === "assigned" ? new Set(rows.filter((r) => r.visitDay === day).map((r) => r.beatId)) : new Set());
  }, [rows, day, mode]);

  const dayLabel = (value) => VISIT_DAYS.find((d) => d.value === value)?.label || value;

  const visible = rows
    .filter((r) => (mode === "assigned" ? r.visitDay : !r.visitDay))
    .sort((a, b) => {
      if (mode === "assigned") {
        const aOn = a.visitDay === day ? 0 : 1;
        const bOn = b.visitDay === day ? 0 : 1;
        if (aOn !== bOn) return aOn - bOn;
      }
      return a.beatName.localeCompare(b.beatName, undefined, { sensitivity: "base" });
    });

  const toggle = (beatId) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(beatId)) next.delete(beatId);
      else next.add(beatId);
      return next;
    });

  const save = async () => {
    const onDay = rows.filter((r) => r.visitDay === day).map((r) => r.beatId);
    let beatIds;
    if (mode === "assigned") {
      beatIds = Array.from(checked);
    } else {
      if (checked.size === 0) {
        toast.error("Select at least one beat to assign");
        return;
      }
      beatIds = Array.from(new Set([...onDay, ...checked]));
    }
    setSaving(true);
    try {
      const data = await api.put(`${RECEIVABLES_API}/beat-day-assignments/${day}`, { beatIds });
      setRows(Array.isArray(data) ? data : []);
      toast.success(`Beats for ${dayLabel(day)} updated`);
    } catch (error) {
      toast.error(error.message || "Failed to save assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading || saving} label={saving ? "Saving..." : "Loading beats..."} />
      <div className="page-panel" style={{ maxWidth: 760 }}>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
          {VISIT_DAYS.map((d) => (
            <label key={d.value} style={{ display: "inline-flex", gap: 6, alignItems: "center", cursor: "pointer" }}>
              <input type="radio" name="day" checked={day === d.value} onChange={() => setDay(d.value)} />
              {d.label}
              <span className="badge badge-gray">{rows.filter((r) => r.visitDay === d.value).length}</span>
            </label>
          ))}
        </div>
        <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="radio" name="mode" checked={mode === "assigned"} onChange={() => setMode("assigned")} />
            Assigned beats
          </label>
          <label style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            <input type="radio" name="mode" checked={mode === "unassigned"} onChange={() => setMode("unassigned")} />
            Unassigned beats ({rows.filter((r) => !r.visitDay).length})
          </label>
        </div>

        <div style={{ maxHeight: "55vh", overflowY: "auto", border: "1px solid var(--color-border)", borderRadius: 8 }}>
          {visible.length === 0 ? (
            <div className="empty-state">
              <span className="empty-state-title">{mode === "assigned" ? "No beats have a day yet" : "Every beat has a day"}</span>
            </div>
          ) : (
            visible.map((row) => (
              <label
                key={row.beatId}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderBottom: "1px solid #f1f1f1" }}
              >
                <input type="checkbox" checked={checked.has(row.beatId)} onChange={() => toggle(row.beatId)} />
                <span style={{ flex: 1 }}>{row.beatName}</span>
                <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{row.salesmanName || "No salesman"}</span>
                {mode === "assigned" && row.visitDay !== day ? (
                  <span className="badge badge-amber">{dayLabel(row.visitDay)}</span>
                ) : null}
              </label>
            ))
          )}
        </div>

        <div className="modal-actions">
          <button className="primary" onClick={save} disabled={saving}>
            {mode === "assigned" ? `Save ${dayLabel(day)} beats` : `Assign to ${dayLabel(day)}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayAssignment;
